import React, { useEffect, useState } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Checkbox } from '@progress/kendo-react-inputs'
import ButtonHLPure from '../buttonHL/buttonHL.pure'
import { Grid } from '@material-ui/core'
import LabelPure from '../label/label.pure'
import { useTranslation } from 'react-i18next'
import { UserService, LocalStorageService } from '../../../services'
import ModalUtil from '../modal/modal.util'
import { useGlobalState } from '../../../GlobalState'
import './dialog-assistant-selection.css'

const DialogAssistantSelection = ({ 
    onClose, 
    isFirstLogin = false,
    showGlobalState = true,
    assistantsList = [],
    currentAssistant = null,
    onCustomSave = null
}) => {
    const localStorageService = new LocalStorageService()
    const userService = new UserService(process.env.REACT_APP_ETICKET_API)
    const modalUtil = new ModalUtil()
    const { t } = useTranslation()
    const { t: tCommon } = useTranslation('common')
    
    // CORREGIDO: Siempre llamar el hook, sin condiciones
    const globalState = useGlobalState()
    const { setBotConfig, chatbotList } = globalState || {}
    
    const [assistantSelected, setAssistantSelected] = useState(null)
    const [setAsDefault, setSetAsDefault] = useState(false)

    // Definir qué lista de asistentes usar
    const assistantsToUse = showGlobalState ? chatbotList : assistantsList
    const currentAssistantToUse = showGlobalState ? null : currentAssistant

    const handleAssistantSelected = (e) => {
        setAssistantSelected(e.value)
    }

    const handleSetAsDefault = (e) => {
        setSetAsDefault(e.value)
    }

    const onSave = async () => {
    try {
        if (!assistantSelected) {
            modalUtil.showModal('error', tCommon('selectAssistant'), t('modal.pure.title.error'))
            return
        }

        const selectedAssistant = assistantsToUse.find(bot => bot.id === assistantSelected.id)
        
        if (!selectedAssistant) {
            throw new Error("Assistant not found")
        }

        // Si está marcado para setear como default
        if (setAsDefault && showGlobalState) {
            try {
                // Mostrar loading
                modalUtil.showModal('loading', t("modal.pure.loading.message"), t("modal.pure.loading.title"))
                const tokenData = JSON.parse(localStorage.getItem('token'))
                const token = tokenData?.idToken
                
                if (!token) throw new Error("Token not found")
                
                // Llamar al backend para actualizar el asistente default
                await userService.updateDefaultBot(selectedAssistant.id, token)
                
                // Actualizar userData en localStorage
                const userData = JSON.parse(localStorage.getItem('user'))
                if (userData) {
                    userData.userMetaData.userA3Cloud.defaultBotConfigId = selectedAssistant.id
                    localStorage.setItem('user', JSON.stringify(userData))
                }
                await modalUtil.showModal('success', tCommon('setAsDefaultSuccess') || 'Asistente establecido como predeterminado', t("modal.pure.title.success"))
            } catch (error) {
                console.error('Error updating default bot:', error)
                await modalUtil.showModal('error', error.message || tCommon('error'), t('modal.pure.title.error'))
                return
            }
        }

        // Ejecutar callback personalizado si existe (para el profile)
        if (typeof onCustomSave === 'function') {
            await onCustomSave(selectedAssistant.id)
        } 
        // O actualizar el GlobalState si estamos en el login
        else if (showGlobalState && setBotConfig) {
            setBotConfig(selectedAssistant)
            localStorageService.setLocalStorageItem('selectedAssistant', selectedAssistant)
        }

        // Cerrar el dialog DESPUÉS de completar todo
        onClose()
    } catch (error) {
        console.error(error)
        modalUtil.showModal('error', error.message || t('modal.pure.title.error'), t('modal.pure.title.error'))
    } finally {
        // Cerrar el modal de loading si está abierto
        onClose()
    }
}

    useEffect(() => {
        if (showGlobalState && chatbotList && chatbotList.length > 0) {
            const activeAssistant = chatbotList.find(bot => bot.active) || chatbotList[0]
            if (activeAssistant) {
                setAssistantSelected({
                    id: activeAssistant.id,
                    label: activeAssistant.chatbotName || activeAssistant.name
                })
            }
        } else if (!showGlobalState && currentAssistantToUse) {
            setAssistantSelected({
                id: currentAssistantToUse.id,
                label: currentAssistantToUse.chatbotName || currentAssistantToUse.name
            })
        }
    }, [showGlobalState, chatbotList, currentAssistantToUse])

    if (showGlobalState) {
        const tokenData = JSON.parse(localStorage.getItem('token'))
        const token = tokenData?.accessToken
        const userData = JSON.parse(localStorage.getItem('user'))
        const clientId = userData?.userMetaData?.userA3Cloud?.clientId
        
        if (!token || !clientId) {
            return <></>
        }
    }

    const options = assistantsToUse?.map(bot => ({
        id: bot.id,
        label: bot.chatbotName || bot.name,
    })) || []

    return (
        <Dialog 
            width={400} 
            onClose={onClose} 
            className="dialogFormHLPure-container" 
            title={tCommon('selectAssistantTitle')}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <LabelPure htmlFor="assistant-dropdown" title={tCommon('selectAssistantLabel')} />
                    <DropDownList
                        id="assistant-dropdown"
                        dataItemKey="id"
                        textField="label"
                        data={options}
                        value={assistantSelected}
                        onChange={handleAssistantSelected}
                        className="eticket-dropdown-field"
                    />
                </Grid>

                {/* Checkbox solo aparece cuando showGlobalState es true (login) */}
                {showGlobalState && (
                    <Grid item xs={12}>
                        <Checkbox
                            id="set-as-default-checkbox"
                            label={tCommon('setAsDefaultAssistant') || 'Establecer como asistente predeterminado'}
                            value={setAsDefault}
                            onChange={handleSetAsDefault}
                        />
                    </Grid>
                )}

                <Grid item xs={12} className='button-actions'>
                    <ButtonHLPure 
                        nameBtn={tCommon('cancel')} 
                        onClick={onClose}
                        colorScheme="neutral"
                    />
                    <ButtonHLPure 
                        primary
                        nameBtn={tCommon('select')}
                        onClick={onSave}
                        disabled={!assistantSelected}
                    />
                </Grid>
            </Grid>
        </Dialog>
    )
}

export default DialogAssistantSelection